import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { verifyFillInBlanks, Question, ExerciseConfig } from '../../../common/verifyFillInBlanks';
import SpeechButton from '../../common/SpeechButton';
import QuestionProgressControls from './QuestionProgressControls';

interface QuestionStudentProps {
  question: Question;
  questionNumber: number;
  answer: string;
  onAnswerChange: (questionId: string, answer: string) => void;
  feedback?: { isCorrect: boolean };
  disabled: boolean;
}

const QuestionStudent: React.FC<QuestionStudentProps> = ({
  question,
  questionNumber,
  answer,
  onAnswerChange,
  feedback,
  disabled
}) => {
  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onAnswerChange(question.id, e.target.value);
  };

  const answerBox = (
    <TextField
      variant="standard"
      size="small"
      sx={{ 
        width: '150px', 
        marginX: 0.5,
        '& .MuiInput-root': {
          fontSize: 'inherit',
        },
        '& .MuiInput-input': {
          textAlign: 'center',
          padding: '0 4px',
        },
        '& .MuiInput-underline:before': {
          borderBottomColor: 'rgba(0, 0, 0, 0.42)',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottomColor: 'rgba(0, 0, 0, 0.87)',
        },
      }}
      value={answer}
      onChange={handleAnswerChange}
      disabled={disabled}
    />
  );

  // Split hints if they're combined with pipe separator (continuous mode)
  const hints = question.hint ? question.hint.split(' | ') : [];

  const formattedText = question.text.split('__').reduce((acc, part, index, array) => {
    acc.push(<span key={`text-${index}`}>{part}</span>);
    if (index < array.length - 1) {
      acc.push(<span key={`input-${index}`}>{answerBox}</span>);
      // Add corresponding hint after each blank
      if (hints[index]) {
        acc.push(
          <Typography 
            key={`hint-${index}`} 
            component="span" 
            variant="caption" 
            color="textSecondary" 
            sx={{ ml: 0.5, fontStyle: 'italic' }}
          >
            ({hints[index]})
          </Typography>
        );
      }
    }
    return acc;
  }, [] as React.ReactNode[]);

  return (
    <Box mb={2}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Kysymys {questionNumber}
      </Typography>
      <Box display="flex" alignItems="baseline" flexWrap="wrap">
        <Typography variant="body1" component="div" sx={{ display: 'inline-flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
          {formattedText}
        </Typography>
        {feedback && (
          <Box ml={1} display="inline-flex" alignItems="center">
            {feedback.isCorrect ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
          </Box>
        )}
        <Box ml={1} display="inline-flex">
          <SpeechButton 
            id={question.id} 
            text={question.text.replace(/__/g, answer)} 
            voiceCode={question.voiceCode || 'en-US-JennyNeural'} // Use question's voiceCode or default to Jenny
          />
        </Box>
      </Box>
    </Box>
  );
};

interface FillInBlanksStudentProps {
  config: ExerciseConfig;
  initialAnswers: { [key: string]: string };
  disabled: boolean;
  onAnswerChange: (questionId: string, answer: string) => void;
  assignmentStatus: 'New' | 'Active' | 'InReview';
  hasSubmitted: boolean;
}

const FillInBlanksStudent: React.FC<FillInBlanksStudentProps> = ({
  config,
  initialAnswers,
  disabled,
  onAnswerChange,
  assignmentStatus,
  hasSubmitted,
}) => {
  console.log('Render FillInBlanksStudent');
  const [userAnswers, setUserAnswers] = useState<{ [key: string]: string }>(initialAnswers);
  const [feedback, setFeedback] = useState<{ questionId: string; isCorrect: boolean }[]>([]);
  const [questionViewMode, setQuestionViewMode] = useState<'all' | 'single'>('all');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    setUserAnswers(initialAnswers); // Reset answers if initial answers change
  }, [initialAnswers]);

  useEffect(() => {
    setCurrentQuestionIndex((previousIndex) => {
      if (config.questions.length === 0) {
        return 0;
      }

      return Math.min(previousIndex, config.questions.length - 1);
    });
  }, [config.questions.length]);

  useEffect(() => {
    // Calculate feedback when the assignment is in review or after submission
    if ((assignmentStatus === 'InReview' || hasSubmitted) && config.questions.length) {
      const verificationResults = verifyFillInBlanks(
        Object.entries(userAnswers).map(([questionId, answer]) => ({ questionId, answer })),
        config
      );
      setFeedback(verificationResults);
    }
  }, [userAnswers, assignmentStatus, hasSubmitted, config]);

  const handleAnswerChange = (questionId: string, answer: string) => {
    setUserAnswers(prev => ({ ...prev, [questionId]: answer }));
    onAnswerChange(questionId, answer);
  };

  const shouldShowFeedback = assignmentStatus === 'InReview' || hasSubmitted;
  const answeredFlags = useMemo(
    () => config.questions.map((question) => Boolean((userAnswers[question.id] || '').trim())),
    [config.questions, userAnswers],
  );
  const visibleQuestions = questionViewMode === 'single'
    ? config.questions.slice(currentQuestionIndex, currentQuestionIndex + 1).map((question, index) => ({
      question,
      index: currentQuestionIndex + index,
    }))
    : config.questions.map((question, index) => ({ question, index }));

  return (
    <Box mt={2}>
      <QuestionProgressControls
        totalQuestions={config.questions.length}
        answeredFlags={answeredFlags}
        currentQuestionIndex={currentQuestionIndex}
        viewMode={questionViewMode}
        onViewModeChange={setQuestionViewMode}
        onPrevious={() => setCurrentQuestionIndex((currentValue) => Math.max(0, currentValue - 1))}
        onNext={() => setCurrentQuestionIndex((currentValue) => Math.min(config.questions.length - 1, currentValue + 1))}
      />
      {visibleQuestions.map(({ question, index }) => {
        const feedbackItem = feedback.find(f => f.questionId === question.id);
        return (
          <QuestionStudent
            key={question.id}
            question={question}
            questionNumber={index + 1}
            answer={userAnswers[question.id] || ''}
            onAnswerChange={handleAnswerChange}
            feedback={shouldShowFeedback && feedbackItem ? { isCorrect: feedbackItem.isCorrect } : undefined}
            disabled={assignmentStatus === 'InReview' || hasSubmitted}
          />
        );
      })}
    </Box>
  );
};

export default FillInBlanksStudent;
